import type { LeaveYearChangeMode } from '@gokrew/models/leave-cycles';
import type { HandoverPlanPayload } from '@gokrew/models/handovers';
import type { ReportingPerson } from '@gokrew/models/reporting';
import type { ManageTeamSelection } from '@/utils/manageTeam';
import type { Gender, User } from './user';
import type { Designation } from './designation';
import type { Department } from './department';
import type { GetListResponse } from './generic';
import type { OptionalPageLimit, PaginationMeta } from './common';
import type { AllocationTypeResponse } from '@gokrew/models/leave-management';
import type { Branch } from './branches';
import type { UploadedFile } from '@/components/form/Multiplefileupload';
import type { ApiResponse } from '@gokrew/models/generic';
import {
  BulkImportJobStatus,
  EmployeeCreationStatus,
  type BulkRowIssue,
  type EmployeeReportingManager,
  type EmployeeTerminationType,
  type SalaryReason,
} from '@gokrew/models/employee';
import type { TerminationReasonCategory } from '@gokrew/models/workforce-analytics';
import type {
  ExpectedRecall,
  RoeReasonCode,
  TerminationRoeInputs,
} from '@gokrew/models/payroll-export';

/**
 * Employee Type Definition
 */
export interface Employee {
  personalEmail?: string | null;
  id: string;
  userId: string;
  designationId: string;
  branchId: string;
  departmentId: string;
  companyId: string;
  employeeReferenceId: string | null;
  status: boolean;
  dateOfJoining: Date | undefined;
  leaveEligibleFrom: Date | undefined;
  employmentType: string | null;
  contractEndDate?: Date | undefined;
  partTimeWeeklyMinutes?: number | null;
  shirtSize?: string | null;
  pantsSize?: string | null;
  pantsLength?: string | null;
  workEmail: string | null;
  workPhone: string | null;
  terminatedAt: string | null;
  createdByUserId: string;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  user: User;
  branch?: Branch;
  designation: Designation;
  department: Department; // TDB, mocking as designation for now as structure is same
  lastInvitationSentAt: string | null;
  createdByUser?: Pick<User, 'id' | 'firstName' | 'lastName'>;
  probationPeriod?: number;
  probationStatus?: EmployeeProbationStatus | null;
  probationEndDate?: Date | undefined;
  managers?: {
    id: string;
    manager: {
      id: string;
      user: Pick<User, 'id' | 'firstName' | 'lastName' | 'email'>;
    };
  }[];
  /** Current reporting-line manager (permanent or interim), or null. */
  reportingManager?: EmployeeReportingManager | null;
  statusMeta?: EmployeeStatusMeta;
  pendingChangeRequestsCount?: number;
}

export type SortBy = 'name' | 'dateOfJoining' | null;
export type SortOrder = 'ASC' | 'DESC' | null;

export type GetEmployeeListResponse = GetListResponse<Employee>;

export interface EmployeeStatusMeta {
  color: 'red' | 'green' | 'yellow' | 'orange' | 'gray';
  meaning:
    | 'inactiveUser'
    | 'activeAndLoggedInUser'
    | 'activeNeverLoggedInInvitationNotSentUser'
    | 'activeNeverLoggedInInvitationSentUser'
    | 'noEmailUser';
}

export const LOGIN_STATUS_OPTIONS = {
  include: 'include',
  exclude: 'exclude',
  only: 'only',
} as const;

export const INVITATION_STATUS_OPTIONS = {
  include: 'include',
  exclude: 'exclude',
  only: 'only',
} as const;

/** Employee list filter: people with an email, or without one (D162). */
export const EMAIL_STATUS_OPTIONS = {
  has_email: 'has_email',
  no_email: 'no_email',
} as const;

/** Employee list filter: whose leave goes to custom approvers (D45). */
export const APPROVER_ROUTING_OPTIONS = {
  custom: 'custom',
  manager: 'manager',
} as const;

export interface GetEmployeeListParams extends OptionalPageLimit {
  status?: boolean | undefined;
  search?: string;
  /** Only these employees (at most 100): display lookups by id. */
  employeeIds?: string[] | undefined;
  designationIds?: string[] | undefined;
  departmentIds?: string[] | undefined;
  branchId?: string;
  employeeType?: string | undefined;
  managerIds?: string[] | undefined;
  joiningDateRange?: { fromDate: string; toDate: string };
  loginStatus?: string | undefined;
  invitationStatus?: string | undefined;
  /** `has_email` or `no_email` (D162). */
  emailStatus?: string | undefined;
  /** true: only people with custom leave approvers; false: only without. */
  hasCustomApprovers?: boolean;
  sortBy?: SortBy;
  sortOrder?: SortOrder;
}

/**
 * Who a batch invite reaches: the people named, or everyone not invited yet
 * (found by the server, whatever the page) minus the ones unticked.
 */
export type BatchInvitePayload =
  | { employeeIds: string[] }
  | { allPending: true; excludeEmployeeIds: string[] };

export type EmployeeStatus = 'all' | 'active' | 'inactive';

export interface GetEmployeeByIdResponse {
  message: string;
  data: Employee;
}

export interface EmployeePersonalInformation {
  personalEmail?: string | null;
  id?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: Date | undefined;
  phoneNumber: string | null;
  email: string;
  /** No email (spec 2026-10-15): the account has a placeholder address. */
  hasNoEmail?: boolean;
  /** When the invitation last went out; empty when never invited. */
  lastInvitationSentAt?: string | null;
  /** Empty when the person never set one. */
  gender?: Gender;
  profileImage: string | null;
  /** The person's own language (emails, portal); empty when never set. */
  language?: string;
  /** Uniform sizes, free text, kept on the employee record. */
  shirtSize?: string | null;
  pantsSize?: string | null;
  pantsLength?: string | null;
}

export interface EmployeeWorkInformation {
  id?: string;
  employeeReferenceId?: string | null;
  departmentId?: string | null;
  designationId?: string | null;
  dateOfJoining?: Date | undefined;
  leaveEligibleFrom?: Date | undefined;
  /** Leave years only: how a new hire date treats the open leave year. */
  leaveYearChange?: LeaveYearChangeMode;
  status?: boolean;
  branchId?: string;
  workEmail?: string | null;
  workPhone?: string | null;
  employmentType?: string | null;
  contractEndDate?: Date | null;
  partTimeWeeklyMinutes?: number | null;
  /** Form-only: the weekly hours as typed, sent as `partTimeWeeklyMinutes`. */
  partTimeWeeklyHours?: number | null;
  terminatedAt?: string | null;
  probationPeriod?: number | null;
  probationEndDate?: Date | null;
  branch?: Branch | null;
  designation?: Designation | null;
  department?: Department | null;
}

export interface EmployeeEducationDocument {
  id: string;
  fileUrl: string;
}

export enum EmployeeProbationStatus {
  Active = 'active',
  Completed = 'completed',
  Extended = 'extended',
}
export interface EmployeeEducation {
  id?: string;
  employeeId: string;
  educationLevel: string;
  institutionName: string;
  course: string;
  startDate: string | null;
  endDate: string | null;
  createdByUserId?: string;
  updatedByUserId?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
  deletedAt?: string | null;
  documents?: EmployeeEducationDocument[];
}

export interface EmployeeFamily {
  id?: string;
  employeeId: string;
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  isEmergencyContact?: boolean;
  relation: string;
  /** ISO date string — the API sends and takes it as text, never a `Date`. */
  dateOfBirth: string | undefined;
  gender: string;
  email: string | undefined;
  extraData: string;
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
}

export interface GetEmployeeEducationListResponse {
  message: string;
  data: EmployeeEducation[];
}

export type CreateEmployeeEducationResponse = ApiResponse<EmployeeEducation>;

export interface GetEmployeeFamilyListResponse {
  message: string;
  data: EmployeeFamily[];
}

export type EmployeeFamilyFormValues = Pick<
  EmployeeFamily,
  | 'name'
  | 'firstName'
  | 'lastName'
  | 'phoneNumber'
  | 'isEmergencyContact'
  | 'relation'
  | 'gender'
  | 'dateOfBirth'
  | 'email'
>;

export type EmployeeEducationFormValues = {
  educationLevel: string;
  institutionName: string;
  course?: string;
  startDate?: string | null;
  endDate?: string | null;
  documents?: string[];
};

export interface AddEmployee {
  user: {
    email: string;
    firstName: string;
    lastName: string;
    /** Optional personal phone, ten digits (the profile's phone number). */
    phoneNumber?: string;
    /** The new person's language: their invitation and emails use it. */
    language?: string;
    /** No email: no portal, no app, no notifications (D157). */
    hasNoEmail?: boolean;
    /** Attach the record to this administrator's account (D159). */
    linkUserId?: string;
  };
  designationId?: string;
  departmentId?: string;
  employeeReferenceId?: string;
  employmentType?: string;
  contractEndDate?: string;
  partTimeWeeklyMinutes?: number | null;
  dateOfJoining?: string;
  workPhone?: string;
  workEmail?: string;
  branchId?: string;
  leaveEligibleFrom?: string;
  isProbationEnabled?: boolean;
  probationPeriod?: number;
  /** The hired applicant this employee is created from; linked on save. */
  recruitingApplicationId?: string;
  /** Email the welcome message now; forced off for a person with no email. */
  sendInvitationMail?: boolean;
}

export type AddEmployeeFormValues = Pick<
  AddEmployee,
  | 'user'
  | 'dateOfJoining'
  | 'designationId'
  | 'departmentId'
  | 'employeeReferenceId'
  | 'employmentType'
  | 'contractEndDate'
  | 'workEmail'
  | 'workPhone'
  | 'branchId'
  | 'leaveEligibleFrom'
  | 'isProbationEnabled'
  | 'probationPeriod'
> & {
  /** Form-only: the weekly hours as typed, sent as `partTimeWeeklyMinutes`. */
  partTimeWeeklyHours?: number | null;
  /**
   * Form-only: the onboarding template started in full once the employee is
   * saved; empty for none. Never sent with the employee.
   */
  onboardingTaskListId?: string;
  /**
   * Form-only ("Manages a team?"): when on, the people in `teamSelection`
   * report to the new employee right after the save. Never sent with the
   * employee.
   */
  managesTeam?: boolean;
  teamSelection?: ManageTeamSelection;
  /** Form-only: the new employee's own manager, set right after the save. */
  reportsTo?: ReportingPerson | null;
};

/** The recruiting job a hire was for: prefills Job Title and Department. */
export interface HireJobPrefill {
  title: string;
  titleFr?: string | null;
  department?: string | null;
}

/** A hired applicant the Add employee form is prefilled from. */
export interface HireCandidate {
  fullName: string;
  email: string;
  /** With it, the server links the application to the new employee. */
  applicationId?: string;
  job?: HireJobPrefill;
  /**
   * The company location the job is at: the form's Location starts on it
   * when the admin may pick it.
   */
  branchId?: string;
  /**
   * The signed offer (e-signatures, spec 7.5): its start date prefills Date
   * of joining, and its salary can become the first salary record.
   */
  offer?: HireOfferPrefill;
}

export interface HireOfferPrefill {
  startDate: string;
  /** Only when the viewer may read salaries. */
  salary: { amount: number | string; currency: string; payType: string } | null;
}

/**
 * A login to create the employee file on (D159, admin access spec
 * 2026-10-31): an administrator of this company with no employee file.
 */
export interface LinkAccountPrefill {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
}

/** What the Add employee form reports once the employee is saved. */
export interface EmployeeCreatedEvent {
  employeeId: string;
  firstName: string;
  lastName: string;
  hasJoiningDate: boolean;
  /** The onboarding workflow picked on the form, or null for none. */
  onboardingWorkflow: { id: string; name: string } | null;
}
export const EMPLOYMENT_TYPE_MAP: Record<string, string> = {
  full_time: 'Full Time',
  part_time: 'Part Time',
  contract: 'Contract',
  intern: 'Intern',
};

export const EDUCATION_LEVEL_MAP: Record<string, string> = {
  high_school: 'High School',
  diploma: 'Diploma',
  bachelor: 'Bachelor',
  master: 'Masters',
  phd: 'PHD',
};

/** Every part of an address is optional; an empty province is sent as null. */
export interface EmployeeAddress {
  id?: string;
  isPermanent?: boolean;
  addressLine_1: string;
  addressLine_2: string;
  city: string;
  provinceId: string | number | null;
  countryCode: string;
  postalCode: string;
}

export interface GetEmployeeAddress {
  message: string;
  data: EmployeeAddress[];
}

export interface EmployeeLeave {
  id: string;
  employeeId: string;
  requestType: string; // e.g., "Sick Leave", "Casual Leave"
  from: string; // start date
  to: string; // end date
  status: 'Approved' | 'Rejected' | 'Pending';
  comments?: string;
}

export interface CompanyDocumentType {
  id: string;
  companyId: string;
  name: string;
  status: boolean;
  description: string;
  documents?: string[];
  /** Whether documents of this type carry an expiry date. */
  hasExpiry?: boolean;
  /** Set on a type every company has, e.g. `work_permit` (D197). */
  defaultKey?: string | null;
}

export interface GetCompanyDocumentType {
  message: string;
  data: CompanyDocumentType[];
}

export type EducationDocumentUploadedFile = UploadedFile & {
  existing?: boolean;
  fileKey?: string;
};

export type AddCompanyDocumentTypeFormValues = Pick<
  CompanyDocumentType,
  'name' | 'description'
>;

export interface EmployeeDocument {
  id: string;
  employeeId: string;
  companyId: string;
  documentTypeId: string;
  status: string;
  documentUrl: string;
  /** `YYYY-MM-DD`; renewal reminders count from it. */
  expiresOn?: string | null;
  documentType?: CompanyDocumentType;
  /** Set on a document filed by an e-signature request (spec 6.5). */
  extraData?: SignedDocumentExtraData | null;
  createdAt: string;
}

/** What a filed signed document carries: its request and evidence files. */
export interface SignedDocumentExtraData {
  signatureRequestId?: string;
  /** The template it was sent from; absent for an uploaded PDF. */
  templateId?: string | null;
  signed?: boolean;
  title?: string;
}

export interface GetEmployeeDocuments {
  messagse: string;
  data: EmployeeDocument[];
}

export enum PayType {
  HOURLY = 'hourly',
  WEEKLY = 'weekly',
  BI_WEEKLY = 'bi_weekly',
  SEMI_MONTHLY = 'semi_monthly',
  MONTHLY = 'monthly',
  ANNUAL = 'annual',
}

export interface EmployeeSalary {
  id: string;
  employeeId: string;
  amount: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  payType: PayType;
  currency: string;
  notes?: string | null;
  /** Why the pay changed (set by Adjust compensation). */
  reason?: SalaryReason | null;
  /** Hours worked per week this pay is for; null follows the schedule. */
  weeklyHours?: number | null;
  createdAt: string;
  updatedAt: string;
  createdByUserId: string;
  updatedByUserId?: string | null;
}

export type EmployeeSalaryFormValues = Pick<
  EmployeeSalary,
  'amount' | 'effectiveFrom' | 'effectiveTo' | 'payType' | 'currency' | 'notes'
>;

export type GetEmployeeSalaryListResponse = GetListResponse<EmployeeSalary>;

export interface EmployeeImportJobsParams {
  companyId: string;
  page?: number;
  limit?: number;
}

export interface ImportJobResponse {
  id: string;
  fileName: string;
  title: string;
  description?: string;
  status: BulkImportJobStatus;
  totalCount: number;
  successCount: number;
  failedCount: number;
  /** Imported rows that carry a warning (e.g. manager not set). */
  warningCount: number;
  createdAt: string;
  updatedAt: string;
  createdByUser?: Pick<User, 'id' | 'firstName' | 'lastName' | 'email'>;
}

export interface ImportJobApiResponse {
  message: string;
  data: {
    data: ImportJobResponse[];
    meta: PaginationMeta;
  };
}

export interface UploadCsvFormValues {
  file: File | null;
}
export interface UploadCsvFormProps {
  companyId: string;
}

export interface EmployeeImportJobDataParams extends OptionalPageLimit {
  companyId: string;
  jobId: string;
  status?: EmployeeCreationStatus;
}

export interface ImportJobRowData {
  email: string;
  firstname: string;
  lastname: string;
  branch: string;
  department: string;
  designation: string;
  employeereferenceid: string;
  dateofbirth: string;
  dateofjoining: string;
  /** Optional Manager Email column. */
  manageremail?: string | null;
  /** Optional No Email column (yes / no). */
  noemail?: string | null;
  /** Optional Gender column (M / F). */
  gender?: string | null;
  /** Optional uniform size columns. */
  shirtsize?: string | null;
  pantssize?: string | null;
  pantslength?: string | null;
  /** Optional starting salary columns. */
  salary?: string | null;
  paytype?: string | null;
  salaryeffectivedate?: string | null;
  /** Optional contact columns. */
  phonenumber?: string | null;
  address?: string | null;
  city?: string | null;
  province?: string | null;
  country?: string | null;
  postalcode?: string | null;
}

export interface ImportJobRow {
  id: string;
  status: EmployeeCreationStatus;
  data: ImportJobRowData;
  /**
   * Per CSV column (plus `_system`, and `_billing` for a row the billing
   * paywall refused): the reason a row failed, or a warning or
   * note on an imported row. Older rows hold a plain English string.
   */
  errorMessage: Record<string, BulkRowIssue | string> | null;
  updatedAt: string;
  employeeId: string | null;
}

export type ImportJobDataApiResponse = GetListResponse<ImportJobRow>;

export interface ImportJobStats {
  totalCount: number;
  successCount: number;
  failedCount: number;
  pendingCount: number;
  warningCount: number;
  status: BulkImportJobStatus;
}

export interface GetEligiblePoliciesParams {
  departmentId?: string | null;
  designationId?: string | null;
  dateOfJoining?: string;
  leaveEligibleFrom?: string;
  branchId?: string;
}

export type LeaveAllocationMethod = 'PRO_RATA' | 'FULL' | 'CUSTOM' | 'NONE';

export interface LeaveAllocationState {
  [policyLeaveTypeId: string]: {
    method: LeaveAllocationMethod;
    customValue?: number;
  };
}

export interface LeaveTypePreview {
  id: string;
  name: string;
  allocationType: AllocationTypeResponse;
  totalLeaves: number;

  proRata: {
    calculatedFrom: string;
    leaves: number;
  } | null;

  effectiveStartDate?: string;
  previousScheduleDate?: string | null;
  nextScheduleDate?: string | null;
}

export interface LeavePolicyPreview {
  id: string;
  name: string;
  leaveTypes: LeaveTypePreview[];
}

export interface LeavePolicyChangeResponse {
  potentialAddedPolicies: LeavePolicyPreview[];
  potentialDeletedPolicies: DeletedPolicyWalletData[];
}

export interface grantInitialLeavesPayload {
  employeeId: string;
  allocations: {
    policyLeaveTypeId: string;
    method: string;
    customValue?: number;
  }[];
}

export interface EligiblePoliciesAfterUpdateApiResponse {
  message: string;
  data: LeavePolicyChangeResponse;
}

export type RevocationWalletMethod = 'ARCHIVE' | 'RESET' | 'RETAIN';

export type WalletRevocationAction = {
  policyId: string;
  leaveTypeId: string;
  revocationWalletMethod: RevocationWalletMethod;
};

export type DeletedPolicyWalletData = {
  policyId: string;
  policyName: string;
  wallets: {
    leaveTypeId: string;
    leaveName: string;
    available: number;
    taken: number;
  }[];
};

export interface UpdateWalletAndGrantResponse {
  message: string;
}

export interface UpdateWalletAndGrantPayload {
  updateWallets?: {
    policyId: string;
    leaveTypeId: string;
    revocationWalletMethod: RevocationWalletMethod;
  }[];
  allocations?: {
    policyLeaveTypeId: string;
    method: LeaveAllocationMethod;
    customValue?: number;
  }[];
}

// Termination
export enum EmployeeTerminationMode {
  IMMEDIATE = 'immediate',
  SCHEDULED = 'scheduled',
}

export enum EmployeeTerminationStatus {
  PENDING = 'pending',
  EXECUTED = 'executed',
  CANCELLED = 'cancelled',
  SUPERSEDED = 'superseded',
  FAILED = 'failed',
}

export interface EmployeeTerminationRequest {
  id: string;
  employeeId: string;
  companyId: string;
  branchId: string;

  mode: EmployeeTerminationMode;
  status: EmployeeTerminationStatus;

  reason: string;

  terminationEffectiveDate: string;

  scheduledFor?: string | null;

  /** ROE inputs (D94); null on terminations recorded before. */
  roeReasonCode?: TerminationRoeInputs['roeReasonCode'];
  lastDayWorked?: TerminationRoeInputs['lastDayWorked'];
  expectedRecall?: TerminationRoeInputs['expectedRecall'];
  expectedRecallDate?: TerminationRoeInputs['expectedRecallDate'];

  executedAt?: string | null;
  cancelledAt?: string | null;

  createdAt: string;
  updatedAt: string;
}

export interface CreateTerminationPayload {
  mode: EmployeeTerminationMode;
  terminationDate: string | Date;
  terminationType: EmployeeTerminationType;
  reason: string;
  /** Fixed reason for workforce analytics; must fit the type. */
  reasonCategory?: TerminationReasonCategory;
  /** ROE inputs (D94), optional until payroll v1 ships. */
  roeReasonCode?: RoeReasonCode;
  expectedRecall?: ExpectedRecall;
  /** `YYYY-MM-DD`; defaults to the termination date on the server. */
  lastDayWorked?: string;
  /** `YYYY-MM-DD`, when `expectedRecall` is `date`. */
  expectedRecallDate?: string;
  /** ROE block 17 (needs employee_salary.update) and block 18 comment. */
  payInLieuAmount?: number;
  severanceAmount?: number;
  roeComment?: string;
  scheduledFor?: string | Date | null;
  /** Where the leaver's reports, approvals and work go (handover step). */
  handover?: HandoverPlanPayload;
}

export interface UpdateTerminationPayload {
  terminationDate?: string | Date | null;
  scheduledFor?: string | Date | null;
  reason?: string;
}

export type GetEmployeeTerminationsResponse =
  GetListResponse<EmployeeTerminationRequest>;
